import { createFileRoute, Link } from "@tanstack/react-router";
import { FileText } from "lucide-react";
import { i18nLinks, i18nOgMeta } from "@/lib/seo";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — Toolverse" },
      {
        name: "description",
        content:
          "Toolverse terms of service: usage rules, intellectual property, disclaimers, and your responsibilities as a user.",
      },
      { property: "og:title", content: "Terms of Service — Toolverse" },
      {
        property: "og:description",
        content:
          "Toolverse terms of service: usage rules, intellectual property, disclaimers, and your responsibilities.",
      },
      { name: "robots", content: "index, follow" },
      ...i18nOgMeta("/terms"),
    ],
    links: [...i18nLinks("/terms")],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="flex-1 bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-5 py-12 md:py-16">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <FileText className="h-3 w-3 text-primary" />
            <span className="text-[11px] font-semibold text-primary tracking-wide uppercase">
              Terms
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">
            Terms of Service
          </h1>
          <p className="text-sm text-muted-foreground">
            Last updated: March 2026
          </p>
        </div>

        <div className="space-y-10 text-sm md:text-base text-muted-foreground leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              1. Acceptance of Terms
            </h2>
            <p>
              By accessing or using Toolverse (toolverse.me), you agree to be
              bound by these Terms of Service. If you do not agree to these
              terms, you may not use the service. These terms apply to all
              visitors, users, and others who access or use the platform.
            </p>
            <p className="mt-3">
              We may update these terms from time to time. Your continued use of
              Toolverse after any changes constitutes acceptance of the updated
              terms. We encourage you to review this page periodically.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              2. Description of Service
            </h2>
            <p>
              Toolverse is a platform offering a collection of specialized web
              tools. Our current suite includes F1 Fantasy analytics tools:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2 mt-3">
              <li>
                Apex Team — an AI-powered optimizer that finds the
                highest-scoring F1 Fantasy team combinations within your budget.
              </li>
              <li>
                Budget Boost — price change predictions for drivers and
                constructors based on historical performance data and the
                official pricing algorithm.
              </li>
              <li>
                Statistics — comprehensive driver and constructor leaderboards
                with race results, fantasy points, price history, and season
                performance.
              </li>
              <li>
                Standings — current F1 World Championship standings for drivers
                and constructors.
              </li>
              <li>
                Fantasy Points — race-by-race fantasy point breakdowns for every
                driver and constructor.
              </li>
            </ul>
            <p className="mt-3">
              Additional tools may be added over time. Each tool is built for a
              specific purpose and operates independently within the platform.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              3. User Accounts
            </h2>
            <p>
              Account creation is optional. You may create an account using your
              email address or through third-party authentication providers
              (Google, Facebook). When you create an account, you are
              responsible for:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2 mt-3">
              <li>
                Maintaining the confidentiality of your login credentials.
              </li>
              <li>All activities that occur under your account.</li>
              <li>Providing accurate and up-to-date information.</li>
              <li>
                Notifying us immediately if you suspect unauthorized access to
                your account.
              </li>
            </ul>
            <p className="mt-3">
              We reserve the right to suspend or terminate accounts that violate
              these terms or are used for unauthorized purposes.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              4. Acceptable Use
            </h2>
            <p className="mb-3">
              You agree not to use Toolverse for any unlawful or prohibited
              purpose. Specifically, you may not:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>
                Use automated tools, bots, or scrapers to access or collect data
                from the platform.
              </li>
              <li>
                Attempt to reverse engineer, decompile, or disassemble any part
                of the service.
              </li>
              <li>
                Interfere with or disrupt the service, servers, or networks
                connected to the platform.
              </li>
              <li>
                Circumvent any access restrictions, rate limits, or security
                measures.
              </li>
              <li>
                Use the service to transmit malware, spam, or any harmful
                content.
              </li>
              <li>
                Resell, redistribute, or commercially exploit the service or its
                data without written permission.
              </li>
              <li>
                Impersonate any person or entity, or misrepresent your
                affiliation with any person or entity.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              5. Intellectual Property
            </h2>
            <p>
              The Toolverse platform, including its design, user interface,
              algorithms, scoring models, optimization engines, and all original
              content, is the intellectual property of Toolverse and is
              protected by applicable copyright and intellectual property laws.
            </p>
            <p className="mt-3">
              Formula 1, F1, the F1 logo, and related marks are trademarks of
              Formula One Licensing B.V. F1 Fantasy is a product of Formula One
              Digital Media Limited. All F1-related data displayed on Toolverse
              is sourced from official Formula 1 and F1 Fantasy sources and
              remains the property of their respective owners.
            </p>
            <p className="mt-3">
              You may not copy, modify, distribute, or create derivative works
              based on our platform without prior written consent.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              6. Disclaimers
            </h2>
            <p className="mb-3">
              Toolverse and all its tools are provided "as is" and "as
              available" without warranties of any kind, either express or
              implied. Specifically:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>
                Predictions, projections, and analytics (including price change
                predictions, expected points, and optimal team suggestions) are
                estimates based on statistical models. They are not guarantees
                of future outcomes.
              </li>
              <li>
                Toolverse is not affiliated with, endorsed by, or connected to
                Formula 1, the FIA, Formula One Management, or the official F1
                Fantasy game in any way.
              </li>
              <li>
                Nothing on this platform constitutes financial, investment, or
                gambling advice. Any decisions you make based on our tools are
                your own responsibility.
              </li>
              <li>
                We do not guarantee the accuracy, completeness, or timeliness of
                any data displayed on the platform.
              </li>
              <li>
                We do not guarantee uninterrupted or error-free access to the
                service.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              7. Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by applicable law, Toolverse and
              its operators shall not be liable for any indirect, incidental,
              special, consequential, or punitive damages, including but not
              limited to loss of profits, data, use, goodwill, or other
              intangible losses, resulting from:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2 mt-3">
              <li>Your use or inability to use the service.</li>
              <li>Any unauthorized access to or alteration of your data.</li>
              <li>Any content or data obtained from or through the service.</li>
              <li>
                Any decisions made based on predictions, analytics, or other
                information provided by the service.
              </li>
            </ul>
            <p className="mt-3">
              In no event shall our total liability exceed the amount you have
              paid to Toolverse in the twelve (12) months preceding the claim,
              or $100 USD, whichever is greater.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              8. Third-Party Services
            </h2>
            <p className="mb-3">
              Toolverse integrates with and relies on various third-party
              services to operate. These include:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>
                Google Analytics — for understanding usage patterns and
                improving the platform.
              </li>
              <li>
                Google AdSense — for displaying advertisements to support the
                free tier. Ad content is determined by Google and may be
                personalized based on your browsing activity.
              </li>
              <li>
                Authentication providers — Google and Facebook for account
                login.
              </li>
              <li>Polar.sh — for processing subscription payments.</li>
              <li>
                Hosting and CDN providers — for delivering the service reliably
                and performantly.
              </li>
            </ul>
            <p className="mt-3">
              Each third-party service operates under its own terms of service
              and privacy policy. We are not responsible for the practices of
              these third parties.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              9. Changes to Terms
            </h2>
            <p>
              We reserve the right to modify or replace these Terms of Service
              at any time. When we make changes, we will update the "Last
              updated" date at the top of this page. Material changes may be
              communicated through the platform.
            </p>
            <p className="mt-3">
              Your continued use of Toolverse after any modifications to these
              terms constitutes your acceptance of the revised terms. If you do
              not agree to the new terms, please discontinue use of the service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3">
              10. Contact
            </h2>
            <p>
              If you have questions about these Terms of Service, please contact
              us at contact@toolverse.me. Toolverse is an independent project
              and is not affiliated with Formula 1, the FIA, or the official F1
              Fantasy game.
            </p>
          </section>
        </div>

        <div className="pt-8 border-t border-border mt-10">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            &larr; Back to Toolverse
          </Link>
        </div>
      </div>
    </div>
  );
}
